import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, 
  Zap, 
  Clock,
  Shield,
  Target,
  DollarSign,
  Globe,
  Rocket,
  ArrowRight,
  Check,
  X,
  ChevronRight,
  Brain,
  Code2,
  Database,
  Eye,
  Settings,
  FlaskConical,
  Cpu,
  Cloud,
  GitBranch,
  Layers,
  Terminal,
  Box,
  BarChart3,
  Send,
  Loader,
  Building2
} from 'lucide-react';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ============= REQUEST ENGINEERS MODAL =============
const RequestEngineersModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    role: '',
    engineers: '',
    skills: '',
    duration: '',
    startDate: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.company || !formData.engineers) {
      setStatus({ type: 'error', message: 'Please fill in all required fields' });
      return;
    }

    setStatus({ type: 'loading', message: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || 'Not provided',
          interest: 'AI Engineer Staff Augmentation',
          message: `Engineer Request\n\nCompany: ${formData.company}\nRole: ${formData.role || 'Not specified'}\n\nNumber of Engineers: ${formData.engineers}\nRequired Skills: ${formData.skills || 'Not specified'}\nEngagement Duration: ${formData.duration || 'Not specified'}\nDesired Start Date: ${formData.startDate || 'Not specified'}\n\nProject Details:\n${formData.message || 'No additional details'}`
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus({ type: 'success', message: 'Request submitted successfully!' });
        setFormData({ name: '', email: '', phone: '', company: '', role: '', engineers: '', skills: '', duration: '', startDate: '', message: '' });
      } else {
        throw new Error(data.error || 'Failed to submit');
      }
    } catch (error) {
      console.error('Request error:', error);
      setStatus({ type: 'error', message: 'Failed to submit. Please try again.' });
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (status.type === 'error') setStatus({ type: '', message: '' });
  };

  const resetModal = () => {
    setStatus({ type: '', message: '' });
    setFormData({ name: '', email: '', phone: '', company: '', role: '', engineers: '', skills: '', duration: '', startDate: '', message: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative bg-slate-950 backdrop-blur-xl border border-white/[0.08] rounded-3xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-emerald-500/5 rounded-3xl" />
        
        <button
          onClick={resetModal}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition z-10"
        >
          <X size={24} />
        </button>

        {status.type === 'success' ? (
          <motion.div 
            className="text-center py-8 relative z-10"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-teal-500/30">
              <Check size={32} className="text-teal-300" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Request Received!</h3>
            <p className="text-gray-400 mb-6">
              Our talent team will review your requirements and match you with qualified engineers within 24 hours.
            </p>
            <button
              onClick={resetModal}
              className="px-6 py-2 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition"
            >
              Close
            </button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="relative z-10">
            <div className="mb-2 inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-blue-500/20 to-emerald-500/20 border border-teal-500/30 rounded-full">
              <Users size={14} className="text-teal-400" />
              <span className="text-xs text-teal-400 font-semibold">72H Deployment</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Request Engineers</h2>
            <p className="text-teal-400 mb-6">Scale your AI team on-demand</p>

            {status.type === 'error' && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {status.message}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Work Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your work email"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Company Name *</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => handleChange('company', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your company name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Engineers Needed *</label>
                  <select
                    value={formData.engineers}
                    onChange={(e) => handleChange('engineers', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                  >
                    <option value="" className="bg-slate-950">Select</option>
                    <option value="1" className="bg-slate-950">1</option>
                    <option value="2-3" className="bg-slate-950">2-3</option>
                    <option value="4-5" className="bg-slate-950">4-5</option>
                    <option value="6-10" className="bg-slate-950">6-10</option>
                    <option value="10+" className="bg-slate-950">10+</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Duration</label>
                  <select
                    value={formData.duration}
                    onChange={(e) => handleChange('duration', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                  >
                    <option value="" className="bg-slate-950">Select</option>
                    <option value="1-3 months" className="bg-slate-950">1-3 months</option>
                    <option value="3-6 months" className="bg-slate-950">3-6 months</option>
                    <option value="6-12 months" className="bg-slate-950">6-12 months</option>
                    <option value="12+ months" className="bg-slate-950">12+ months</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Required Skills</label>
                <select
                  value={formData.skills}
                  onChange={(e) => handleChange('skills', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                >
                  <option value="" className="bg-slate-950">Select primary skill</option>
                  <option value="ML Architect" className="bg-slate-950">ML Architect</option>
                  <option value="NLP Engineer" className="bg-slate-950">NLP Engineer</option>
                  <option value="Computer Vision" className="bg-slate-950">Computer Vision</option>
                  <option value="Data Scientist" className="bg-slate-950">Data Scientist</option>
                  <option value="MLOps Engineer" className="bg-slate-950">MLOps Engineer</option>
                  <option value="AI Research Engineer" className="bg-slate-950">AI Research Engineer</option>
                  <option value="Full Stack ML" className="bg-slate-950">Full Stack ML</option>
                  <option value="Multiple Skills" className="bg-slate-950">Multiple Skills</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Project Details</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition resize-none"
                  placeholder="Describe your project and requirements..."
                />
              </div>

              <button
                type="submit"
                disabled={status.type === 'loading'}
                className="w-full py-3 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {status.type === 'loading' ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Submit Request
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};

// ============= NAVBAR =============
const Navbar = ({ onRequest }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-xl bg-slate-950/40 border-b border-white/5' : 'backdrop-blur-sm bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-teal-500" />
            <span className="text-white">AI Factory</span>
          </Link>

          <div className="hidden md:flex items-center gap-12">
            <Link to="/" className="text-sm text-gray-300 hover:text-white transition">Home</Link>
            <Link to="/services" className="text-sm text-gray-300 hover:text-white transition">Services</Link>
            <a href="#talent" className="text-sm text-gray-300 hover:text-white transition">Talent</a>
            <a href="#process" className="text-sm text-gray-300 hover:text-white transition">Process</a>
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onRequest}
            className="hidden md:block px-6 py-2.5 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-sm font-semibold text-white hover:shadow-lg hover:shadow-teal-500/50 transition"
          >
            Request Engineers
          </motion.button>
        </div>
      </div>
    </motion.nav>
  );
};

// ============= GRADIENT ORBS BACKGROUND =============
const GradientOrbs = () => {
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      <motion.div
        className="absolute -top-40 -left-40 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, 30, -30, 0],
          y: [0, 40, -40, 0],
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute top-1/2 -right-40 w-96 h-96 bg-emerald-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, -30, 30, 0],
          y: [0, -40, 40, 0],
        }}
        transition={{ duration: 10, repeat: Infinity }}
      />
      <motion.div
        className="absolute -bottom-40 left-1/2 w-96 h-96 bg-teal-600/15 rounded-full blur-3xl"
        animate={{
          x: [0, 40, -40, 0],
          y: [0, 30, -30, 0],
        }}
        transition={{ duration: 12, repeat: Infinity }}
      />
    </div>
  );
};

// ============= HERO SECTION =============
const Hero = ({ onRequest }) => {
  const stats = [
    { value: '500+', label: 'Elite Engineers' },
    { value: '72H', label: 'Deployment Time' },
    { value: '98%', label: 'Retention Rate' },
    { value: '24/7', label: 'Global Coverage' },
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      <GradientOrbs />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(20,184,166,0.2),rgba(255,255,255,0))]" />

      <motion.div
        className="relative z-10 text-center max-w-5xl mx-auto px-4 py-20"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Badges */}
        <motion.div
          className="flex flex-wrap justify-center gap-3 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {['Elite Talent', 'Rapid Deployment', 'Zero Overhead'].map((badge, i) => (
            <span
              key={i}
              className="text-xs font-semibold text-slate-900 px-4 py-2 rounded-full bg-gradient-to-r from-teal-400 to-emerald-400"
            >
              {badge}
            </span>
          ))}
        </motion.div>

        {/* Title */}
        <motion.h1
          className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight tracking-tight"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <span className="text-white">AI Engineer</span>
          <br />
          <span className="bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent">
            Augmentation
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          className="text-xl md:text-2xl text-gray-300 mb-4 font-light tracking-wide"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          On-Demand AI Engineering Excellence
        </motion.p>

        {/* Description */}
        <motion.p
          className="text-base md:text-lg text-gray-400 mb-10 max-w-3xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          Scale your AI capabilities instantly with battle-tested engineers. Deploy ML architects, 
          data scientists, and AI specialists who integrate seamlessly into your team—no hiring delays, 
          no training overhead, just pure engineering power when you need it.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onRequest}
            className="px-8 py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-2xl hover:shadow-teal-500/50 transition flex items-center gap-2"
          >
            Request Engineers <ArrowRight size={18} />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => document.getElementById('talent').scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-4 border border-white/20 rounded-full text-white font-semibold hover:bg-white/10 transition backdrop-blur-sm hover:border-teal-400/40"
          >
            View Talent Pool
          </motion.button>
        </motion.div>

        {/* Stats */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="relative group"
              whileHover={{ y: -5 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-teal-500/20 to-blue-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-teal-500/30 transition-all">
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-400 uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

// ============= TALENT CARD =============
const TalentCard = ({ talent, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  const icons = {
    'brain': Brain,
    'code': Code2,
    'eye': Eye,
    'database': Database,
    'settings': Settings,
    'flask': FlaskConical
  };

  const Icon = icons[talent.icon] || Brain;

  return (
    <motion.div
      className="relative group h-full"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      viewport={{ once: true, margin: "-100px" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 via-transparent to-emerald-500/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:border-teal-400/30 transition-all duration-500 h-full flex flex-col overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-t-3xl" />
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-teal-500/10 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        <div className="relative z-10 flex flex-col h-full">
          <div className="mb-4">
            <span className="text-xs font-semibold text-teal-400 uppercase tracking-wider">
              {talent.level}
            </span>
          </div>

          <div className="flex items-center gap-4 mb-4">
            <motion.div
              className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/30 to-emerald-500/30 flex items-center justify-center group-hover:from-blue-500/50 group-hover:to-emerald-500/50 transition-all"
              animate={isHovered ? { rotate: [0, -10, 10, 0] } : {}}
              transition={{ duration: 0.5 }}
            >
              <Icon className="text-teal-300" size={28} />
            </motion.div>
            <h3 className="text-xl font-bold text-white group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:via-teal-400 group-hover:to-emerald-400 group-hover:bg-clip-text transition-all">
              {talent.title}
            </h3>
          </div>

          <p className="text-gray-400 mb-6 leading-relaxed flex-grow">
            {talent.description}
          </p>

          <ul className="space-y-3">
            {talent.skills.map((skill, i) => (
              <motion.li
                key={i}
                className="text-sm text-gray-400 flex items-start gap-3"
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * i }}
              >
                <Zap size={14} className="text-emerald-400 mt-1 flex-shrink-0" />
                <span>{skill}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
};

// ============= TALENT SECTION =============
const TalentSection = () => {
  const talents = [
    {
      icon: 'brain',
      level: 'Senior Level',
      title: 'ML Architect',
      description: 'Design and implement scalable machine learning systems. Build end-to-end ML pipelines from data ingestion to model deployment and monitoring.',
      skills: [
        'TensorFlow, PyTorch, JAX ecosystem mastery',
        'Distributed training & model optimization',
        'MLOps: MLflow, Kubeflow, Vertex AI',
        'Feature engineering & pipeline orchestration',
        'Model serving: TFServing, TorchServe, Triton'
      ]
    },
    {
      icon: 'code',
      level: 'Senior Level',
      title: 'NLP Engineer',
      description: 'Build sophisticated language models and conversational AI systems. Expert in transformer architectures, fine-tuning, and production NLP deployment.',
      skills: [
        'LLM fine-tuning: GPT, BERT, T5, LLaMA',
        'Prompt engineering & instruction tuning',
        'RAG systems & vector databases',
        'Text generation, summarization, classification',
        'Hugging Face, LangChain, LlamaIndex'
      ]
    },
    {
      icon: 'eye',
      level: 'Senior Level',
      title: 'Computer Vision',
      description: 'Develop advanced vision systems for object detection, segmentation, and image analysis. Deploy real-time vision models at scale.',
      skills: [
        'CNN architectures: ResNet, EfficientNet, ViT',
        'Object detection: YOLO, R-CNN, DETR',
        'Image segmentation & instance detection',
        'Video analysis & tracking systems',
        'Edge deployment: TensorRT, ONNX, CoreML'
      ]
    },
    {
      icon: 'database',
      level: 'Senior Level',
      title: 'Data Scientist',
      description: 'Extract insights from complex datasets, build predictive models, and drive data-driven decision making with advanced statistical and ML techniques.',
      skills: [
        'Statistical modeling & hypothesis testing',
        'Time series forecasting & anomaly detection',
        'A/B testing & experimentation design',
        'Feature engineering & selection strategies',
        'Python, R, SQL, Spark ecosystem'
      ]
    },
    {
      icon: 'settings',
      level: 'Senior Level',
      title: 'MLOps Engineer',
      description: 'Build production ML infrastructure, automate model deployment, and ensure reliable monitoring and maintenance of AI systems at scale.',
      skills: [
        'CI/CD for ML: GitHub Actions, Jenkins',
        'Kubernetes, Docker, cloud platforms',
        'Model versioning & experiment tracking',
        'Monitoring: Prometheus, Grafana, DataDog',
        'Infrastructure as code: Terraform, Ansible'
      ]
    },
    {
      icon: 'flask',
      level: 'Senior Level',
      title: 'AI Research Engineer',
      description: 'Implement cutting-edge research papers, prototype novel architectures, and push the boundaries of what\'s possible with AI technology.',
      skills: [
        'Paper implementation & reproduction',
        'Novel architecture design & experiments',
        'Reinforcement learning: PPO, SAC, DQN',
        'GANs, diffusion models, VAEs',
        'Academic collaboration & publication'
      ]
    }
  ];

  return (
    <section id="talent" className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Talent Arsenal
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Engineer Specializations
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg">
            Access world-class AI engineering talent across all specializations. From ML infrastructure 
            to production deployment, every engineer is pre-vetted, battle-tested, and ready to deliver.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {talents.map((talent, index) => (
            <TalentCard key={index} talent={talent} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= TECH STACK SECTION =============
const TechStackSection = () => {
  const techItems = [
    'TensorFlow', 'PyTorch', 'JAX', 'Hugging Face', 'LangChain', 'Scikit-Learn',
    'XGBoost', 'LightGBM', 'Keras', 'OpenCV', 'CUDA', 'Docker',
    'Kubernetes', 'AWS SageMaker', 'GCP Vertex AI', 'Azure ML', 'Ray', 'Spark',
    'Airflow', 'MLflow', 'Kubeflow', 'DVC', 'Weights & Biases', 'Pinecone',
    'Weaviate', 'FastAPI', 'Streamlit'
  ];

  return (
    <section className="relative py-24 px-4 overflow-hidden bg-slate-950/50">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Tech Stack
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Engineering Arsenal
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Our engineers master the entire AI technology landscape—from frameworks and tools 
            to cloud platforms and deployment infrastructure.
          </p>
        </motion.div>

        <motion.div
          className="flex flex-wrap justify-center gap-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          {techItems.map((tech, index) => (
            <motion.div
              key={index}
              className="group"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: index * 0.02 }}
              viewport={{ once: true }}
              whileHover={{ y: -5, scale: 1.05 }}
            >
              <div className="px-5 py-3 bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-xl text-sm font-medium text-teal-400 hover:border-teal-500/50 hover:bg-gradient-to-br hover:from-teal-500/10 hover:to-blue-500/10 transition-all cursor-default">
                {tech}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ============= PROCESS SECTION =============
const ProcessSection = () => {
  const steps = [
    {
      number: '01',
      title: 'Requirements Scan',
      description: '30-minute technical assessment to understand your project requirements, tech stack, team structure, and timeline. We map the exact skills and experience needed.'
    },
    {
      number: '02',
      title: 'Talent Matching',
      description: 'AI-powered matching system screens our pre-vetted talent pool. Within 24 hours, you receive 3-5 engineer profiles perfectly aligned with your requirements.'
    },
    {
      number: '03',
      title: 'Technical Interview',
      description: 'Direct technical interviews with shortlisted candidates. Evaluate coding skills, problem-solving approach, and cultural fit before making your selection.'
    },
    {
      number: '04',
      title: 'Instant Deployment',
      description: 'Selected engineer joins your team within 48 hours. Full onboarding support, documentation access, and seamless integration into your existing workflows.'
    }
  ];

  return (
    <section id="process" className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Deployment Process
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Rapid Integration Protocol
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            From initial contact to production deployment in 72 hours. Our streamlined process 
            ensures your team gets the right talent without the traditional hiring friction.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              className="relative group"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:border-teal-500/30 transition-all duration-300 h-full text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-500 via-teal-500 to-emerald-500 flex items-center justify-center text-slate-950 text-2xl font-bold shadow-lg shadow-teal-500/30">
                  {step.number}
                </div>
                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-teal-400 transition-colors">
                  {step.title}
                </h3>
                <p className="text-gray-400 leading-relaxed text-sm">
                  {step.description}
                </p>
                <div className="mt-6 h-1 w-0 group-hover:w-full bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full transition-all duration-500 mx-auto" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= COMPARISON SECTION =============
const ComparisonSection = () => {
  const comparisons = [
    { metric: 'Time to Deploy', traditional: '3-6 months', augmentation: '72 hours' },
    { metric: 'Recruitment Costs', traditional: '$15K-$30K per hire', augmentation: '$0' },
    { metric: 'Onboarding Time', traditional: '4-8 weeks', augmentation: '2-3 days' },
    { metric: 'Training Investment', traditional: 'Significant', augmentation: 'Minimal' },
    { metric: 'Scalability', traditional: false, augmentation: true },
    { metric: 'Flexibility', traditional: false, augmentation: true },
    { metric: 'Trial Period Risk', traditional: 'High', augmentation: 'None' },
    { metric: 'Benefits/Overhead', traditional: '30-40% extra', augmentation: 'Included' },
  ];

  return (
    <section className="relative py-24 px-4 overflow-hidden bg-slate-950/50">
      <div className="max-w-5xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Value Proposition
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Why Staff Augmentation Wins
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Compare traditional hiring against our on-demand engineering model. The difference 
            in speed, cost, and flexibility speaks for itself.
          </p>
        </motion.div>

        <motion.div
          className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="grid grid-cols-3 gap-4 p-6 bg-gradient-to-r from-teal-500/20 to-blue-500/20 border-b border-white/10">
            <div className="text-white font-bold">Metric</div>
            <div className="text-white font-bold text-center">Traditional Hiring</div>
            <div className="text-white font-bold text-center">AI Augmentation</div>
          </div>

          {comparisons.map((row, index) => (
            <div
              key={index}
              className="grid grid-cols-3 gap-4 p-6 border-b border-white/5 hover:bg-white/[0.02] transition-colors"
            >
              <div className="text-gray-300">{row.metric}</div>
              <div className="text-center">
                {typeof row.traditional === 'boolean' ? (
                  row.traditional ? (
                    <Check className="inline text-emerald-400" size={24} />
                  ) : (
                    <X className="inline text-red-400" size={24} />
                  )
                ) : (
                  <span className="text-gray-400">{row.traditional}</span>
                )}
              </div>
              <div className="text-center">
                {typeof row.augmentation === 'boolean' ? (
                  row.augmentation ? (
                    <Check className="inline text-emerald-400" size={24} />
                  ) : (
                    <X className="inline text-red-400" size={24} />
                  )
                ) : (
                  <span className="text-teal-400 font-semibold">{row.augmentation}</span>
                )}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ============= BENEFITS SECTION =============
const BenefitsSection = () => {
  const benefits = [
    {
      icon: Zap,
      title: 'Instant Scale',
      description: 'Ramp up or down instantly based on project demands. Add 5 engineers for a sprint, scale to 20 for a major release, then optimize back—with zero friction.'
    },
    {
      icon: Target,
      title: 'Precision Skills',
      description: 'Access niche expertise exactly when needed. Need a GAN specialist for 3 months? Or a Kubernetes expert? Get exactly the right skill at the right time.'
    },
    {
      icon: DollarSign,
      title: 'Cost Efficiency',
      description: 'Pay only for productive hours. No recruitment fees, benefits overhead, or idle time. Reduce engineering costs by 40-60% compared to full-time hires.'
    },
    {
      icon: Shield,
      title: 'Zero Risk',
      description: 'Not satisfied? Replace engineers within 48 hours with no penalty. Every placement comes with performance guarantee and unlimited replacements.'
    },
    {
      icon: Rocket,
      title: 'Faster Delivery',
      description: 'Pre-vetted engineers hit the ground running. Skip the learning curve and deliver features 3x faster with battle-tested professionals.'
    },
    {
      icon: Globe,
      title: 'Global Talent',
      description: 'Access worldwide expertise without geographical constraints. Build follow-the-sun teams or find specific regional AI knowledge when needed.'
    }
  ];

  return (
    <section className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Key Benefits
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Strategic Advantages
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Beyond just filling seats—augmentation transforms how you build and scale AI capabilities 
            with flexibility, expertise, and zero risk.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              className="group relative"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:border-teal-500/30 transition-all duration-300 h-full relative overflow-hidden">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-teal-500 to-emerald-500 rounded-l-2xl" />
                <div className="mb-6">
                  <benefit.icon className="w-10 h-10 text-teal-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-teal-400 transition-colors">
                  {benefit.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= FINAL CTA =============
const FinalCTA = ({ onRequest }) => {
  return (
    <section className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-4xl mx-auto">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-teal-500/20 to-emerald-600/20 blur-3xl" />

        <motion.div
          className="relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-12 md:p-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-t-3xl" />

          <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">
            Deploy Elite AI Engineers
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Scale your AI capabilities in 72 hours. Zero risk, maximum flexibility, world-class talent on demand.
          </p>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onRequest}
            className="px-10 py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-bold text-lg hover:shadow-2xl hover:shadow-teal-500/50 transition flex items-center gap-3 mx-auto"
          >
            Request Engineers <ArrowRight size={20} />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

// ============= FOOTER =============
const Footer = () => {
  return (
    <footer className="relative border-t border-white/10 py-12 px-4 bg-slate-950">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <Link to="/" className="text-white font-bold mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-teal-500" />
              AI Factory
            </Link>
            <p className="text-gray-400 text-sm mt-4 leading-relaxed">
              On-demand AI engineering talent for enterprises worldwide.
            </p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Services</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/ai-engineer-augmentation" className="hover:text-teal-400 transition">Staff Augmentation</Link></li>
              <li><Link to="/ai-project-execution" className="hover:text-teal-400 transition">Project Execution</Link></li>
              <li><Link to="/ai-consulting-services" className="hover:text-teal-400 transition">Consulting</Link></li>
              <li><Link to="/custom-ai-ml-solutions" className="hover:text-teal-400 transition">Custom Solutions</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Company</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/#about" className="hover:text-teal-400 transition">About</Link></li>
              <li><Link to="/training" className="hover:text-teal-400 transition">Training Programs</Link></li>
              <li><Link to="/services" className="hover:text-teal-400 transition">All Services</Link></li>
              <li><Link to="/#contact" className="hover:text-teal-400 transition">Contact</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Legal</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><a href="#" className="hover:text-teal-400 transition">Privacy Policy</a></li>
              <li><a href="#" className="hover:text-teal-400 transition">Terms of Service</a></li>
              <li><a href="#" className="hover:text-teal-400 transition">Cookie Policy</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">© 2025 AI Factory. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">Twitter</a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">LinkedIn</a>
            <a href="https://discord.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">Discord</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============= MAIN PAGE =============
export default function AIEngineerAugmentationPage() {
  const [requestModal, setRequestModal] = useState(false);

  const handleRequest = () => {
    setRequestModal(true);
  };

  return (
    <div className="bg-slate-950 min-h-screen overflow-x-hidden text-white">
      <Navbar onRequest={handleRequest} />
      <Hero onRequest={handleRequest} />
      <TalentSection />
      <TechStackSection />
      <ProcessSection />
      <ComparisonSection />
      <BenefitsSection />
      <FinalCTA onRequest={handleRequest} />
      <Footer />

      {/* Request Engineers Modal */}
      <RequestEngineersModal
        isOpen={requestModal}
        onClose={() => setRequestModal(false)}
      />
    </div>
  );
}